<template>
  <communication-log-card
    data-cy="activity-card"
    :background-class="colors.background"
    :comments="log.data.comments"
    class="call-log-card"
  >
    <template #icon>
      <icon-base :icon="IconTelephone" class="text-white" />
    </template>
    <template #content>
      <div
        class="rounded-t-md bg-grey-hover px-4 py-2 font-medium text-gray-500"
      >
        <span class="cursor-pointer" @click="openDeal">
          {{
            log.data.call_sessionable?.business?.business_legal_name ??
            $t("COMMON.DEAL_LINK")
          }}
        </span>
      </div>
      <log-call-content :log="log" />
    </template>
    <template #footer>
      <log-call-footer
        :log="log"
        :icon-color="colors.text"
        :current-color-of-status="currentColorOfStatus"
      />
    </template>
  </communication-log-card>
</template>

<script setup lang="ts">
import { computed, provide } from "vue";
import { useRouter } from "vue-router";

import { CallSessionStatus, LogEndpointType } from "@/enums/communicationLogs";
import { COLORS_BY_DISPOSITION_NOTE_STATUS } from "@/helpers/constants/colors";
import { CommunicationLogContextKey } from "@/helpers/symbols";
import { ROUTE_DEAL_PROGRESS } from "@/router/routes";

import CommunicationLogCard from "@/components/communicationLogs/communication/CommunicationLogCard.vue";
import LogCallContent from "@/components/communicationLogs/communication/LogCallContent.vue";
import LogCallFooter from "@/components/communicationLogs/communication/LogCallFooter.vue";
import IconTelephone from "@/components/icons/IconTelephone.vue";

import type { CallLog } from "@/models/communicationLogs";

interface Props {
  log: CallLog;
}

const DEFAULT_COLORS = {
  background: "bg-gray-100",
  text: "text-gray-500",
} as const;

const STATUS_RELATED_COLORS: Record<
  CallSessionStatus,
  { background: string; text: string }
> = {
  [CallSessionStatus.Unreachable]: {
    background: "bg-error",
    text: "text-error",
  },
  [CallSessionStatus.InProgress]: {
    background: "bg-warning",
    text: "text-warning",
  },
  [CallSessionStatus.Answered]: {
    background: "bg-success",
    text: "text-success",
  },
  [CallSessionStatus.Ringing]: {
    background: "bg-success",
    text: "text-success",
  },
};

const { log } = defineProps<Props>();

provide(CommunicationLogContextKey, {
  type: LogEndpointType.CallSession,
  logId: log.data.id,
});

const router = useRouter();

const callLogData = computed(() => log.data);

const colors = computed(
  () => STATUS_RELATED_COLORS[log.data.status] ?? DEFAULT_COLORS
);

const currentColorOfStatus = computed(() => {
  return COLORS_BY_DISPOSITION_NOTE_STATUS[
    callLogData.value
      ?.call_disposition as keyof typeof COLORS_BY_DISPOSITION_NOTE_STATUS
  ];
});

const dealRoute = computed(() =>
  router.resolve({
    name: ROUTE_DEAL_PROGRESS,
    params: { id: log.data?.call_sessionable.application_id?.toString() },
  })
);

const openDeal = () => {
  if (!log.data?.call_sessionable.application_id) {
    return;
  }
  window.open(dealRoute.value.href, "_blank");
};
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.note-body :deep() {
  a {
    @apply text-primary underline;
  }
}
.menu-item {
  @apply cursor-pointer px-4 py-1 hover:bg-blue-100;
}

.call-log-card {
  container-type: inline-size;
  container-name: call-log-card;
}
.call-footer {
  container-type: inline-size;
  container-name: call-footer;
}

@container call-log-card (max-width: 870px) {
  :deep() {
    .space-x-4 {
      @apply space-x-2;
    }
    .edit-call {
      @apply mt-0! !flex !self-end p-1-5!;
    }
    .comments-toggle {
      @apply ml-0! !self-end;
    }
  }
}

@container call-footer (min-width: 750px) {
  :deep() {
    > .wrapper {
      @apply space-x-2;
    }
  }
}

@container call-footer (max-width: 750px) {
  :deep() {
    > .wrapper {
      @apply -mr-1 -ml-1 flex-wrap;
      > div {
        @apply p-1;
      }
    }
  }
}

.call-recording {
  container-type: inline-size;
  container-name: call-recording;
}

@container call-recording (max-width: 650px) {
  :deep() {
    .recording {
      @apply flex-wrap;
    }
    .w-2\/5,
    .w-3\/5 {
      @apply w-full;
    }
    .w-2\/5 {
      @apply mb-2;
    }
  }
}
</style>
